// Check WebGL Renderer

import { CashRequestMiddleware } from './types'
function getWebGLInfo(): string {
  if (typeof document === 'undefined') {
    return 'no-document'
  }

  const canvas: HTMLCanvasElement = document.createElement('canvas')
  const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
  if (!gl) {
    return 'no-webgl'
  }

  const debugInfo: WEBGL_debug_renderer_info | null = gl.getExtension('WEBGL_debug_renderer_info')
  const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null

  if (!renderer) {
    return 'no-webgl-renderer'
  }

  return renderer
}

// Performance Timing (Jitter detection)
function detectTimingAnomalies(): number | string {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return 'no-performance'
  }

  const start: number = performance.now()
  // eslint-disable-next-line no-empty
  for (let i = 0; i < 1e6; i++) {} // Loop to measure execution timing
  const end: number = performance.now()
  const diff: number = end - start
  return diff // Some VMs have high timing anomalies
}

async function checkWebRTC(): Promise<string> {
  if (typeof window === 'undefined' || !window.RTCPeerConnection) {
    return 'no-webrtc'
  }

  return new Promise(resolve => {
    const timeout = setTimeout(() => resolve('no-webrtc'), 1000) // Timeout after 1s
    try {
      const pc = new RTCPeerConnection()

      // Create a data channel to trigger ICE candidate gathering
      pc.createDataChannel('')

      const candidates: string[] = []

      pc.onicecandidate = event => {
        if (event.candidate) {
          const candidate = event.candidate.candidate.toLowerCase()
          candidates.push(candidate)
        } else {
          // ICE gathering complete
          clearTimeout(timeout)

          pc.close()
          resolve(candidates.join(','))
        }
      }

      // Trigger ICE candidate gathering
      pc.createOffer()
        .then(offer => pc.setLocalDescription(offer))
        .catch(() => {
          clearTimeout(timeout)
          resolve(candidates.join('webrtc-error'))
        })
    } catch {
      clearTimeout(timeout)
      resolve('webrtc-error')
    }
  })
}

// Update getDeviceInfo to handle async detectRemoteTools
async function getDeviceInfo(): Promise<string> {
  const renderer = getWebGLInfo()
  const timingAnomalies = detectTimingAnomalies()
  const rtcCandidates = await checkWebRTC()

  return [renderer, timingAnomalies, rtcCandidates].join('::')
}

let deviceInfo: string | undefined = undefined
getDeviceInfo().then(info => {
  deviceInfo = info
})
// Update middleware to handle async getDeviceInfo
export const deviceInfoRequest: CashRequestMiddleware = async request => {
  if (deviceInfo) {
    request.headers = {
      ...request.headers,
      'X-Web-Device-Info': deviceInfo,
    }
  }
  return request
}

// Export the function
export { getDeviceInfo }
